import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCityContext } from '../../context/CityContext';
import { useNotificationsContext } from '../../context/NotificationsContext';
import Modal from '../../components/UI/Modal';
import SubscriptionForm from '../../components/SubscriptionForm';
import Badge from '../../components/UI/Badge';
import Button from '../../components/UI/Button';
import './MyAlerts.css';

export default function MyAlerts() {
  const { citySlug } = useParams();
  const { cityList, activateCity } = useCityContext();
  const { getSubscriptionsForCity, subscribe, unsubscribe } = useNotificationsContext();

  const [editTopic, setEditTopic] = useState(null);

  const city = cityList.find((c) => c.slug === citySlug);
  const subscriptions = getSubscriptionsForCity(citySlug);

  useEffect(() => {
    if (city) {
      activateCity(city.slug);
      document.title = `My Notifications — Notify ${city.name}`;
    }
  }, [city, activateCity]);

  if (!city) {
    return (
      <div className="my-alerts-empty-page">
        <p>City not found. <Link to="/">Go home</Link></p>
      </div>
    );
  }

  const getTopicById = (id) => city.topics.find((t) => t.id === id);

  const METHOD_ICONS = { email: '📧', sms: '📱', push: '🔔' };

  return (
    <div className="my-alerts">
      {/* Header band */}
      <section className="my-alerts-hero">
        <div className="my-alerts-hero-inner">
          <Link to={`/${citySlug}`} className="my-alerts-back">← Browse Alerts</Link>
          <h1 className="my-alerts-title">My Notifications</h1>
          <p className="my-alerts-sub">
            Manage your alert subscriptions for {city.name}, {city.state}.
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="my-alerts-body">
        <div className="my-alerts-inner">
          {subscriptions.length === 0 ? (
            <div className="my-alerts-empty">
              <div className="my-alerts-empty-icon">🔕</div>
              <h2>No subscriptions yet</h2>
              <p>Head back to browse alerts and subscribe to the notifications that matter to you.</p>
              <Link to={`/${citySlug}`}>
                <Button variant="primary">Browse {city.name} Alerts</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="my-alerts-count">
                {subscriptions.length} active subscription{subscriptions.length !== 1 ? 's' : ''}
              </div>

              <div className="my-alerts-list">
                {subscriptions.map((sub) => {
                  const topic = getTopicById(sub.topicId);
                  if (!topic) return null;
                  return (
                    <div key={sub.topicId} className="my-alerts-item">
                      <div className="my-alerts-item-icon">{topic.icon}</div>
                      <div className="my-alerts-item-info">
                        <div className="my-alerts-item-name">{topic.name}</div>
                        <div className="my-alerts-item-meta">
                          <Badge variant="default">{topic.category}</Badge>
                          <span className="my-alerts-item-date">
                            Subscribed {new Date(sub.subscribedAt).toLocaleDateString()}
                          </span>
                        </div>
                        {sub.address && (
                          <div className="my-alerts-item-address">📍 {sub.address}</div>
                        )}
                        <div className="my-alerts-item-methods">
                          {sub.methods.map((m) => (
                            <span key={m} className="my-alerts-method-chip">
                              {METHOD_ICONS[m] || '📬'} {m === 'sms' ? 'SMS' : m.charAt(0).toUpperCase() + m.slice(1)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="my-alerts-item-actions">
                        <Button variant="outline" size="sm" onClick={() => setEditTopic(topic)}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => unsubscribe(citySlug, topic.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="my-alerts-footer-actions">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => subscriptions.forEach((s) => unsubscribe(citySlug, s.topicId))}
                >
                  Remove All Subscriptions
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit modal */}
      {editTopic && (
        <Modal
          isOpen={!!editTopic}
          onClose={() => setEditTopic(null)}
          title="Manage Subscription"
          size="md"
        >
          <SubscriptionForm
            topic={editTopic}
            citySlug={citySlug}
            existingSubscription={subscriptions.find((s) => s.topicId === editTopic.id)}
            onSubmit={({ methods, contact, address }) => {
              subscribe(citySlug, editTopic.id, { methods, contact, address });
              setEditTopic(null);
            }}
            onUnsubscribe={() => {
              unsubscribe(citySlug, editTopic.id);
              setEditTopic(null);
            }}
            onCancel={() => setEditTopic(null)}
          />
        </Modal>
      )}
    </div>
  );
}
